import React, { useEffect, useState } from 'react';
import OpportunityButton from '../organizations/opportunities/OpportunityButton';
import { Column, Row } from 'react-foundation';
import { CountrySelect } from '../shared/CountrySelect';
import {
	getContact,
	getFieldAttributes,
	postContact,
	putContact,
} from '../../../api/ContactsAPI';
import { getOrganization } from '../../../api/organizationsAPI';

const NewContact = ({ organizations, orgID, contactID, isEdit }) => {
	const contactDataTemplate = {
		first_name: '',
		last_name: '',
		organization_id: orgID,
		title: '',
		city: '',
		state: '',
		country: '',
		email: '',
		phone_number: '',
		organization_relationship: '',
		linked_in_profile: '',
		source: '',
	};

	const [contactData, setContactData] = useState(contactDataTemplate);
	const [requiredFields, setRequiredFields] = useState([]);

	const emptyFieldMessage = 'Field cannot be left blank!';

	/**
	 * On page load, fetch existing contactID information if it exists
	 */
	useEffect(() => {
		if (contactID) {
			fetchExistingContact();
		}
		fetchFieldAttributes();
		getOrganizationByID(orgID);
	}, []);

	const fetchExistingContact = async () => {
		let response = await getContact(contactID);
		let contact = response.data;
		setContactData(contact);

		let countryElement = document.getElementById('country');
		if (countryElement) {
			countryElement.value = contact.country;
		}

		let organizationRelationshipElement = document.getElementById(
			'organization_relationship'
		);
		if (organizationRelationshipElement) {
			organizationRelationshipElement.value =
				contact.organization_relationship;
		}

		let organizationAffiliatedElement =
			document.getElementById('organization_id');
		if (organizationAffiliatedElement) {
			organizationAffiliatedElement.value = contact.organization_id;
		}
	};

	const getOrganizationByID = async (orgID) => {
		if (!orgID) {
			throw new Error('No orgID provided');
		}
		const { data: fetchedOrganiztion } = await getOrganization(orgID);

		if (!isEdit) {
			setContactData({
				...contactData,
				country: fetchedOrganiztion.country,
				city: fetchedOrganiztion.city,
				state: fetchedOrganiztion.state,
			});
		}
	};

	const fetchFieldAttributes = async () => {
		let response = await getFieldAttributes();
		let fieldAttributes = response.data;
		let tempRequiredFields = fieldAttributes.presenceValidations;
		setRequiredFields(tempRequiredFields);

		for (const requiredField of tempRequiredFields) {
			let element = document.getElementById(requiredField + '-header');
			if (element) {
				element.textContent += ' *';
			}
		}
	};

	const handleCancel = () => {
		history.back();
	};

	const handleChange = (event) => {
		contactData[event.target.name] = event.target.value;
		setContactData(contactData);

		if (requiredFields.includes(event.target.name)) {
			if (event.target.value === '') {
				document.getElementById(
					event.target.name + '-empty-label'
				).style.display = 'inline-block';
			} else {
				document.getElementById(
					event.target.name + '-empty-label'
				).style.display = 'none';
			}
		}
	};

	const areRequiredFieldsFilled = () => {
		let allRequiredFieldsFilled = true;
		let hasScrolledAlready = false;

		for (const requiredField of requiredFields) {
			if (contactData[requiredField] === '') {
				allRequiredFieldsFilled = false;
				document.getElementById(
					requiredField + '-empty-label'
				).style.display = 'inline-block';
				if (!hasScrolledAlready) {
					document
						.getElementById(requiredField + '-header')
						.scrollIntoView({ behavior: 'smooth' });
					hasScrolledAlready = true;
				}
			}
		}

		return allRequiredFieldsFilled;
	};

	/**
	 * Handles publishing of the opportunity - linked to the 'publish' button at the bottom of the screen
	 */
	const handleSubmit = async () => {
		if (areRequiredFieldsFilled()) {
			try {
				let response;
				if (contactID) {
					response = await putContact(contactID, contactData);
				} else {
					response = await postContact(contactData);
				}
				if (response.status === 200) {
					location.assign(`/organizations/${orgID}`);
				}
			} catch (e) {
				console.error(e);
			}
		}
	};

	return (
		<div className="new-contact">
			<Row>
				<Column small={12}>
					<OpportunityButton
						text="Back to organization profile"
						className="back-link"
						handleSubmit={handleCancel}
						isBackLink={true}
						leftButtonText="Cancel"
						rightButtonText="Exit"
						headerText="You have unsaved content. Would you like to exit and lose your work?"
					/>
					{contactID ? <h1>Edit Contact</h1> : <h1>New Contact</h1>}

					<h6 className="header-font" id="first_name-header">
						First Name
					</h6>
					<input
						type="text"
						className="body-font-important"
						name="first_name"
						onChange={(e) => handleChange(e)}
						defaultValue={contactData.first_name}
					/>
					<label
						className="empty-field-label"
						id="first_name-empty-label"
					>
						{emptyFieldMessage}
					</label>

					<h6 className="header-font">Last Name</h6>
					<input
						type="text"
						className="body-font-important"
						name="last_name"
						onChange={(e) => handleChange(e)}
						defaultValue={contactData.last_name}
					/>
					<h6 className="header-font" id="organization_id-header">
						Enter the affiliated organization
					</h6>

					<select
						className="body-font"
						onChange={(e) => handleChange(e)}
						defaultValue={orgID}
						name="organization_id"
						id="organization_id"
					>
						<option value="" disabled />
						{organizations.map((organization) => {
							return (
								<option
									key={organization[0]}
									value={organization[0]}
								>
									{organization[1]}
								</option>
							);
						})}
					</select>
					<label
						className="empty-field-label"
						id="organization_id-empty-label"
					>
						{emptyFieldMessage}
					</label>

					<h6 className="header-font">
						Relationship to organization
					</h6>
					<select
						className="body-font"
						defaultValue={contactData.organization_relationship}
						name="organization_relationship"
						onChange={(e) => handleChange(e)}
						id="organization_relationship"
					>
						<option value="" disabled />
						<option>Staff</option>
						<option>Volunteer</option>
					</select>
					<h6 className="header-font">Title</h6>
					<input
						type="text"
						className="body-font-important"
						name="title"
						onChange={(e) => handleChange(e)}
						defaultValue={contactData.title}
					/>

					<h6 className="header-font">City</h6>
					<input
						defaultValue={contactData.city}
						type="text"
						name="city"
						id="city"
						onChange={(e) => handleChange(e)}
					/>

					<h6 className="header-font">Province/State</h6>
					<input
						defaultValue={contactData.state}
						type="text"
						name="state"
						id="state"
						onChange={(e) => handleChange(e)}
					/>

					<h6 className="header-font">Country</h6>
					<CountrySelect
						defaultValue={contactData.country}
						name="country"
						id="country"
						handleChange={handleChange}
					/>
					<h6 className="header-font">Email</h6>
					<input
						type="email"
						className="body-font-important"
						name="email"
						onChange={(e) => handleChange(e)}
						defaultValue={contactData.email}
					/>
					<h6 className="header-font">Phone Number</h6>
					<input
						type="tel"
						className="body-font-important"
						name="phone_number"
						onChange={(e) => handleChange(e)}
						defaultValue={contactData.phone_number}
					/>
					<h6 className="header-font">Linkedin Profile</h6>
					<input
						type="url"
						className="body-font-important"
						name="linked_in_profile"
						onChange={(e) => handleChange(e)}
						defaultValue={contactData.linked_in_profile}
					/>

					<div
						className="primary-button float-right"
						onClick={handleSubmit}
					>
						<p>Save</p>
					</div>
				</Column>
			</Row>
		</div>
	);
};

export default NewContact;
